import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Todo } from '@/infra/types/domain';
import { fetchProjectDetail, ProjectsState } from '../ducks';
import {
  selectActiveProjectId,
  selectChildrenMap,
  selectTodosById,
} from '../selectors';

// ProjectTodoDetailSection — below-title detail slot renderer for the
// projects plugin. Shows which project the todo belongs to, its parent
// task, and any subtasks. Rendered only when the todo has a project_id.
//
// The section reads projects.detail state. If the detail for the todo's
// project isn't loaded yet (e.g. user opened the modal from a different
// surface), it kicks a fetch and renders a skeleton in the meantime.
export function ProjectTodoDetailSection({ todo }: { todo: Todo }) {
  const dispatch = useDispatch();
  const projectId = todo.project_id ?? null;
  const project = useSelector((state: { projects: ProjectsState }) =>
    projectId ? state.projects.projectsById[projectId] ?? null : null,
  );
  const activeProjectId = useSelector(selectActiveProjectId);
  const todosById = useSelector(selectTodosById);
  const childrenMap = useSelector(selectChildrenMap);

  useEffect(() => {
    if (projectId && activeProjectId !== projectId) {
      dispatch(fetchProjectDetail(projectId));
    }
  }, [dispatch, projectId, activeProjectId]);

  if (!projectId) return null;

  const thisTodoInProject = todosById[todo.id];
  const parentId = thisTodoInProject?.parent_todo_id ?? null;
  const parent = parentId ? todosById[parentId] : null;
  const children = childrenMap[todo.id] ?? [];

  const projectHref = `/projects/${projectId}`;
  const closed = (status: string) => status === 'done' || status === 'cancelled';
  const openChildren = children.filter((c) => !closed(c.status)).length;

  return (
    <div className="todo-detail__project-section" data-field="project-context">
      <div className="todo-detail__project-row">
        <span className="todo-detail__project-label">Project</span>
        <Link to={projectHref} className="todo-detail__project-link">
          {project?.title ?? todo.project_title ?? 'Loading project…'}
        </Link>
      </div>
      {parent && (
        <div className="todo-detail__project-row">
          <span className="todo-detail__project-label">Parent task</span>
          <Link to={`${projectHref}?todo=${parent.id}`} className="todo-detail__project-link">
            {parent.title}
          </Link>
        </div>
      )}
      {children.length > 0 && (
        <div className="todo-detail__project-row todo-detail__project-row--children">
          <span className="todo-detail__project-label">
            Subtasks ({children.length - openChildren}/{children.length} done)
          </span>
          <ul className="todo-detail__project-children">
            {children.map((child) => (
              <li key={child.id} className="todo-detail__project-child">
                <span className={`todo-detail__project-child-status todo-detail__project-child-status--${child.status}`}>
                  {closed(child.status) ? '✓' : '○'}
                </span>
                <Link
                  to={`${projectHref}?todo=${child.id}`}
                  className={`todo-detail__project-link${closed(child.status) ? ' todo-detail__project-link--done' : ''}`}
                >
                  {child.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {openChildren > 0 && todo.status !== 'done' && todo.status !== 'cancelled' && (
        <div className="todo-detail__project-blocked" role="status">
          Complete {openChildren} subtask{openChildren === 1 ? '' : 's'} before closing this task.
        </div>
      )}
    </div>
  );
}
